import { Link, Users } from "@scoach/ui/icons";

import { safeHref } from "../../../../lib/safeHref.ts";
import { useLiveMeetingStore } from "../store.ts";

/** Render a 1-3 relevance rating as filled/empty dots. */
function RatingDots({ rating }: { rating: 1 | 2 | 3 }) {
  return (
    <span title={`Relevance ${rating}/3`} style={{ display: "inline-flex", gap: 3 }}>
      {[1, 2, 3].map((n) => (
        <span
          key={n}
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: n <= rating ? "var(--gc-blue, #1a73e8)" : "var(--border, #dadce0)",
          }}
        />
      ))}
    </span>
  );
}

/**
 * "Use Cases" panel — Google Cloud customer stories (cloud.google.com/customers)
 * surfaced live when relevant to the conversation. Mirrors the What's New panel;
 * each card shows the customer, a 1-3 relevance rating, summary, keyword tags and
 * a source link. Clicking a tag filters the hint list (shared activeTagFilter).
 */
export function UseCasesPanel() {
  const useCases = useLiveMeetingStore((s) => s.useCases);
  const activeTag = useLiveMeetingStore((s) => s.activeTagFilter);
  const setActiveTag = useLiveMeetingStore((s) => s.setActiveTagFilter);

  if (useCases.length === 0) {
    return (
      <div className="ig-empty">
        <Users size={28} color="var(--text-4)" />
        <div className="ig-empty-title">Use Cases</div>
        <div className="ig-empty-sub">
          Relevant Google Cloud customer stories will appear here as the call progresses.
        </div>
      </div>
    );
  }

  return (
    <div className="infographic-panel" style={{ minHeight: 0 }}>
      <div
        className="scroll"
        style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 10 }}
      >
        {useCases.map((u) => (
          <div
            key={u.id}
            style={{
              border: "1px solid var(--border, #e0e0e0)",
              borderRadius: 8,
              padding: "10px 12px",
              background: "var(--surface, #fff)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                marginBottom: 6,
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 600, color: "var(--gc-blue, #1a73e8)" }}>
                {u.customer}
                {u.industry ? ` · ${u.industry}` : ""}
              </span>
              <RatingDots rating={u.rating} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3, margin: "2px 0 4px" }}>
              {u.title}
            </div>
            <div style={{ fontSize: 13, color: "var(--text-2, #444)", lineHeight: 1.45 }}>
              {u.summary}
            </div>
            {u.products && u.products.length > 0 && (
              <div style={{ fontSize: 12, color: "var(--text-4)", marginTop: 6 }}>
                Products: {u.products.join(", ")}
              </div>
            )}
            {u.relatedTopic && (
              <div
                style={{ fontSize: 12, color: "var(--text-4)", marginTop: 4, fontStyle: "italic" }}
              >
                Re: {u.relatedTopic}
              </div>
            )}
            {u.tags && u.tags.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                {u.tags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    className={`tag-chip${activeTag === tag ? " on" : ""}`}
                    onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                    title={`Filter hints by "${tag}"`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            )}
            {u.url && (
              <a
                href={safeHref(u.url)}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 12,
                  marginTop: 8,
                  color: "var(--gc-blue, #1a73e8)",
                  textDecoration: "none",
                }}
              >
                <Link size={12} /> Read the story
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
